package com.anjuros.website.redesign.styles

import com.anjuros.website.redesign.theme.RdColor
import com.anjuros.website.redesign.theme.RdFont
import com.anjuros.website.redesign.theme.RdSize
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px

internal fun grid(columns: String) = Modifier.display(DisplayStyle.Grid).css("grid-template-columns" to columns)

// ---------- "Every website includes" band ----------

val RdIncludedStyle = CssStyle.base {
    Modifier
        .backgroundColor(RdColor.SkySoft)
        .css("border-block" to "1px solid rgb(180,198,220)", "padding-block" to "clamp(28px, 4vw, 40px)")
}

val RdIncludedGridStyle = CssStyle {
    base { grid("1fr").alignItems(AlignItems.Center).css("gap" to "20px 40px") }
    Breakpoint.XL { Modifier.css("grid-template-columns" to "auto 1fr") }
}

val RdIncludedTitleStyle = CssStyle {
    base {
        Modifier
            .color(RdColor.Brand)
            .css("font" to "500 .78rem/1.3 ${RdFont.MONO_STACK}", "letter-spacing" to ".12em", "text-transform" to "uppercase")
    }
    Breakpoint.XL { Modifier.css("max-width" to "9ch") }
}

val RdIncludedListStyle = CssStyle {
    base { grid("1fr 1fr").gap(20.px).css("list-style" to "none", "padding" to "0") }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "repeat(3, 1fr)") }
    Breakpoint.XL { Modifier.css("grid-template-columns" to "repeat(5, 1fr)") }
    cssRule(" li") { Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.FlexStart).gap(12.px).css("min-width" to "0") }
}

val RdIncludedTextStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(2.px).color(RdColor.Muted).css("font-size" to ".88rem", "line-height" to 1.4) }
    cssRule(" b") { Modifier.color(RdColor.Ink).css("font-size" to ".95rem", "font-weight" to 600) }
}

// ---------- FAQ ----------

val RdFaqBandStyle = CssStyle.base {
    Modifier.backgroundColor(RdColor.SkySoft).css("border-block" to "1px solid rgb(180,198,220)")
}

val RdFaqGridStyle = CssStyle {
    base { grid("1fr").css("gap" to "clamp(32px, 5vw, 64px)") }
    Breakpoint.MD { Modifier.css("grid-template-columns" to ".8fr 1.2fr") }
}

val RdFaqListStyle = CssStyle {
    base { Modifier.css("border-top" to "1px solid rgb(180,198,220)") }
    cssRule(" details") { Modifier.css("border-bottom" to "1px solid rgb(220,227,236)") }
    cssRule(" summary") {
        Modifier.display(DisplayStyle.Flex).justifyContent(JustifyContent.SpaceBetween).gap(16.px)
            .css("cursor" to "pointer", "list-style" to "none", "padding" to "20px 0", "font" to "700 1.08rem/1.35 ${RdFont.DISPLAY_STACK}", "font-stretch" to "104%")
    }
    cssRule(" summary::-webkit-details-marker") { Modifier.display(DisplayStyle.None) }
    cssRule(" summary::after") {
        // A fixed-size box at the top, not stretched to the question's height, so it turns on the spot
        // even when a long question wraps onto two lines.
        Modifier.css(
            "content" to "\"+\"", "font-family" to RdFont.MONO_STACK, "font-weight" to 500, "font-size" to "1.3rem", "line-height" to 1,
            "flex" to "none", "align-self" to "flex-start", "width" to "1em", "text-align" to "center",
            "transform-origin" to "50% 50%", "transition" to "transform .2s"
        )
    }
    cssRule(" details[open] summary::after") { Modifier.css("transform" to "rotate(45deg)") }
    cssRule(" details p") { Modifier.color(RdColor.Muted).css("padding" to "0 0 22px", "max-width" to "62ch") }
}

// ---------- Hero layout (text left, visual right) ----------

val RdHeroStyle = CssStyle.base {
    Modifier.css("padding-block" to "clamp(40px, 6vw, 80px) clamp(48px, 6vw, 72px)")
}

val RdHeroGridStyle = CssStyle {
    base { grid("1fr").alignItems(AlignItems.Center).css("gap" to "clamp(32px, 5vw, 64px)") }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "1.15fr .85fr") }
}

val RdHeroCopyStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(24.px).css("min-width" to "0")
}

val RdButtonRowStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Flex).gap(12.px).css("flex-wrap" to "wrap")
}

/** Smaller H1 for subpages; the homepage has its own larger title. */
val RdSubpageTitleStyle = CssStyle.base {
    Modifier.fontFamily(*RdFont.DISPLAY).css(
        "font-size" to "clamp(2.2rem, 1.2rem + 3.6vw, 3.8rem)", "font-weight" to 900,
        "font-stretch" to "118%", "line-height" to 1, "letter-spacing" to "-.02em"
    )
}

// ---------- Breadcrumb ----------

val RdBreadcrumbStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Flex).gap(8.px).color(RdColor.Muted)
            .css("flex-wrap" to "wrap", "list-style" to "none", "padding" to "0", "margin" to "0", "font-size" to ".88rem")
    }
    cssRule(" li + li::before") { Modifier.color(RdColor.LogoBlue).css("content" to "\"/\"", "margin-right" to "8px") }
    cssRule(" a") { Modifier.color(RdColor.Muted).textDecorationLine(TextDecorationLine.None) }
    cssRule(" a:hover") { Modifier.color(RdColor.Ink).textDecorationLine(TextDecorationLine.Underline) }
}

// ---------- Dark closing card ----------

val RdNextGridStyle = CssStyle {
    base { grid("1fr").gap(24.px) }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "1fr 1fr") }
}

val RdNextCardStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Grid).gap(14.px).backgroundColor(RdColor.Paper)
            .border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(20.px)
            .css("align-content" to "start", "padding" to "clamp(24px, 4vw, 40px)")
    }
    cssRule(" p") { Modifier.color(RdColor.Muted) }
    cssRule(" > a") { Modifier.css("justify-self" to "start", "margin-top" to "6px") }
}

val RdNextCardDarkStyle = CssStyle {
    base { Modifier.backgroundColor(RdColor.Band).color(RdColor.BandText).css("border-color" to "rgb(34,54,79)") }
    cssRule(" p") { Modifier.color(RdColor.BandMuted) }
    cssRule(" .rd-eyebrow") { Modifier.color(RdColor.BandMuted) }
}

// ---------- Dark closing call to action ----------

val RdClosingStyle = CssStyle {
    base {
        grid("1fr").backgroundColor(RdColor.Band).color(RdColor.BandText).borderRadius(20.px)
            .alignItems(AlignItems.Center).css("gap" to "24px 48px", "padding" to "clamp(28px, 5vw, 56px)")
    }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "1.4fr 1fr") }
    cssRule(" .rd-eyebrow") { Modifier.color(RdColor.BandMuted) }
    cssRule(" .rd-lede") { Modifier.color(RdColor.BandMuted) }
    // On the dark card the primary button switches to logo blue.
    cssRule(" .rd-primary-button") { Modifier.backgroundColor(RdColor.LogoBlue).color(RdColor.Ink) }
    cssRule(" .rd-primary-button:hover") { Modifier.backgroundColor(RdColor.BandText) }
}

val RdClosingButtonsStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).gap(12.px).css("flex-wrap" to "wrap") }
    Breakpoint.MD { Modifier.justifyContent(JustifyContent.FlexEnd) }
}

// ---------- Key facts row ----------

val RdFactsStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .css(
                "flex-wrap" to "wrap", "gap" to "10px 28px", "margin" to "6px 0 0", "padding" to "18px 0 0",
                "border-top" to "1px solid rgb(220,227,236)", "list-style" to "none"
            )
    }
    cssRule(" li") { Modifier.display(DisplayStyle.Grid).gap(2.px) }
    cssRule(" b") {
        Modifier.css("font" to "800 1.5rem/1 ${RdFont.DISPLAY_STACK}", "font-stretch" to "110%", "font-variant-numeric" to "tabular-nums")
    }
    cssRule(" span") { Modifier.color(RdColor.Muted).css("font-size" to ".88rem") }
}

// ---------- Icon badge ----------

/** Pale-blue circle holding a line icon, used on cards. */
val RdIconBadgeStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).backgroundColor(RdColor.SkySoft).color(RdColor.Brand).borderRadius(50.px)
        .css("width" to "40px", "height" to "40px", "place-items" to "center")
}

// ---------- Benefits: titled points in columns ----------

val RdBenefitsGridStyle = CssStyle {
    base { grid("1fr").css("gap" to "36px 40px") }
    Breakpoint.SM { Modifier.css("grid-template-columns" to "1fr 1fr") }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "repeat(3, 1fr)") }
    cssRule(" > div") { Modifier.display(DisplayStyle.Grid).gap(8.px).css("padding-top" to "18px", "border-top" to "2px solid rgb(180,198,220)") }
    cssRule(" p") { Modifier.color(RdColor.Muted).css("font-size" to ".97rem") }
}

// ---------- Numbered steps on a line ----------

/**
 * Phones and tablets stack the steps, so there is no line: each step has its dot with the number beside it.
 * From LG the four steps sit in one row on a line, with each dot on the line above its number.
 */
val RdStepsStyle = CssStyle {
    base {
        grid("1fr").css("counter-reset" to "step", "list-style" to "none", "padding" to "0", "row-gap" to "28px")
    }
    Breakpoint.SM { Modifier.css("grid-template-columns" to "1fr 1fr", "column-gap" to "24px") }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "repeat(4, 1fr)", "row-gap" to "0", "column-gap" to "0", "border-top" to "2px solid rgb(30,40,52)") }
    cssRule(" li") {
        Modifier.position(Position.Relative).display(DisplayStyle.Grid).gap(10.px).css("align-content" to "start")
    }
    cssRule(Breakpoint.LG.toCSSMediaQuery(), " li") { Modifier.css("padding" to "22px 22px 0 0") }
    cssRule(" li::before") {
        Modifier.color(RdColor.Muted).css(
            "counter-increment" to "step", "content" to "\"0\" counter(step)",
            "font" to "500 .8rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".06em",
            // Room for the dot on its left.
            "padding-left" to "24px"
        )
    }
    cssRule(Breakpoint.LG.toCSSMediaQuery(), " li::before") { Modifier.css("padding-left" to "0") }
    cssRule(" li::after") {
        Modifier.backgroundColor(RdColor.LogoBlue).position(Position.Absolute).css(
            "content" to "\"\"", "top" to "-2px", "left" to "0", "width" to "12px", "height" to "12px",
            "border" to "2px solid rgb(30,40,52)", "border-radius" to "50%"
        )
    }
    // On the line above the step.
    cssRule(Breakpoint.LG.toCSSMediaQuery(), " li::after") { Modifier.css("top" to "-7px") }
    cssRule(" p") { Modifier.color(RdColor.Muted).css("font-size" to ".97rem") }
    cssRule(" a") { Modifier.css("font-weight" to 600) }
}
